import { useEffect, useState } from 'react'
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe } from '@stripe/stripe-js'
import type { UUID } from 'crypto'
import MembershipUpgrade from '@/components/MembershipUpgrade'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { COLORS } from '../theme'
import { primaryButtonSx, outlinedButtonSx } from '../styles'

interface SubscribeDialogProps {
  open: boolean
  onClose: () => void
  onLater?: () => void
  skipIntro?: boolean
}

export default function SubscribeDialog({ open, onClose, onLater, skipIntro = false }: SubscribeDialogProps) {
  const { orgUuid } = useOnboardingV2()
  const [wizardData] = useWizardData()
  const { user, token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''

  const [showIntro, setShowIntro] = useState(!skipIntro)
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const stripeKey = import.meta.env.VITE_PUBLIC_STRIPE_KEY as string | undefined

  // Reset intro state when dialog opens
  useEffect(() => {
    if (open) {
      setShowIntro(!skipIntro)
    }
  }, [open, skipIntro])

  useEffect(() => {
    if (open && stripeKey && !stripePromise) {
      setStripePromise(loadStripe(stripeKey))
    }
  }, [open, stripeKey, stripePromise])

  const orgName = wizardData.orgForm?.orgName ?? ''

  const handleClose = () => {
    if (showIntro && onLater) {
      onLater()
    } else {
      onClose()
    }
  }

  if (!stripePromise) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="subscribe-dialog-title"
      PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}
    >
      <DialogTitle id="subscribe-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Subscribe to Fundsorter
        <IconButton onClick={handleClose} size="small" aria-label="Close subscription dialog">
          <CloseIcon aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        <Box aria-live="polite" aria-atomic="true">
        {showIntro ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 3 }}>
            <Typography sx={{ color: COLORS.secondary, textAlign: 'center', maxWidth: 480 }}>
              Upgrade to a Paid plan to unlock unlimited matches, applications, and more.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                disableRipple
                onClick={() => setShowIntro(false)}
                sx={{ ...primaryButtonSx, px: 4 }}
              >
                Subscribe
              </Button>
              <Button
                variant="outlined"
                disableRipple
                onClick={onLater}
                sx={{ ...outlinedButtonSx, px: 4 }}
              >
                Later
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Box sx={{ minHeight: 300 }}>
              <MembershipUpgrade
                orgUUID={(orgUuid as UUID) ?? null}
                orgName={orgName}
                token={token}
                stripePromise={stripePromise}
                onError={setError}
                onSuccess={onClose}
                userEmail={user?.email}
                userFirstName={user?.first_name || undefined}
                userLastName={user?.last_name || undefined}
              />
            </Box>
          </>
        )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
